import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createGifts, getGiftsPaginated, GiftRequest, GiftFilter } from "../../../services/gift.services";
import { handlePromise } from "../../../utils/handlePromisse";
import { initialPagination, PaginatedResponse } from "../../../domain/models/paginated-response.model";
import { GiftModel } from "./table/gift.model";
import { GiftFormHandle } from "./form/gift-form";

/** Estado, fetch e handlers da página de brindes. */
export function useGiftPage() {
    const [searchParams] = useSearchParams();
    const initialStatus = searchParams.get("status") === "unused" ? "unused" : searchParams.get("status") === "used" ? "used" : undefined;

    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState<GiftFilter>({ page: 1, size: 10, status: initialStatus });
    const [searchValue, setSearchValue] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [gifts, setGifts] = useState<PaginatedResponse<GiftModel>>(initialPagination);
    const [modalOpen, setModalOpen] = useState(false);
    const [formValid, setFormValid] = useState(false);
    const [planFilter, setPlanFilter] = useState<string>("all");

    const formRef = useRef<GiftFormHandle>(null);

    const fetchGifts = useCallback(async () => {
        setLoading(true);
        setGifts(initialPagination);
        const [result, error] = await handlePromise(getGiftsPaginated(filter));
        if (!!error) {
            setShowSnackbar(true);
            setSnackbarMessage(error.response.data.message);
            setLoading(false);
            return;
        }
        setGifts(result.data as PaginatedResponse<GiftModel>);
        setLoading(false);
    }, [filter]);

    const onHandleGiftAdd = useCallback(
        async (giftsReq: GiftRequest[]) => {
            setLoading(true);
            const [_, error] = await handlePromise(createGifts(giftsReq));
            setLoading(false);

            if (!!error) {
                setShowSnackbar(true);
                setSnackbarMessage(error.response.data.message);
                return;
            }

            setShowSnackbar(true);
            const plural = `${giftsReq.length > 1 ? "s" : ""}`;
            setSnackbarMessage(`Presente${plural} enviado${plural} com sucesso!`);
            setModalOpen(false);
            fetchGifts();
        },
        [fetchGifts]
    );

    const handlePageChange = useCallback((newPage: number) => {
        setFilter((prev) => ({ ...prev, page: newPage }));
    }, []);

    const handlePageSizeChange = useCallback((newSize: number) => {
        setFilter((prev) => ({ ...prev, size: newSize, page: 1 }));
    }, []);

    const handleSearch = useCallback((value: string) => {
        setSearchValue(value);
        setFilter((prev) => ({ ...prev, search: value, page: 1 }));
    }, []);

    const handleStatusChange = useCallback((value: string) => {
        setFilter((prev) => ({ ...prev, status: value === "all" ? undefined : (value as "used" | "unused"), page: 1 }));
    }, []);

    useEffect(() => {
        fetchGifts();
    }, [fetchGifts]);

    const distinctPlans = Array.from(new Set(gifts.data.map((g) => g.plan).filter(Boolean) as string[]));
    const displayedGifts = planFilter === "all" ? gifts.data : gifts.data.filter((g) => g.plan === planFilter);

    return {
        loading,
        searchValue,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        gifts,
        modalOpen,
        setModalOpen,
        formValid,
        setFormValid,
        planFilter,
        setPlanFilter,
        statusFilter: filter.status ?? "all",
        handleStatusChange,
        formRef,
        distinctPlans,
        displayedGifts,
        onHandleGiftAdd,
        handlePageChange,
        handlePageSizeChange,
        handleSearch,
    };
}
